import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {MeshCellNote} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshViewTargetType} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshViewTargetType';
import {AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {doWithBinding, IBinding} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {PropertiesVisiblePopover} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {EditMenuItems} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topMenu/EditMenuItems';
import {DataSource} from '@mintdata/mint-kit/api/models/List';
import {ContextMenuTarget, IContextMenuItem} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';

function separator():IContextMenuItem
{
    return {
        label: '',
        isSeparator: true
    };
}

function insertColumnsLeftItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Insert ${range.columnsCount} left`,
        handler: () =>
        {
            sheetCtrl.insertColumns(
                sheetId,
                range.from.colIndex,
                range.columnsCount
            );
        }
    };
}

function insertColumnsRightItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Insert ${range.columnsCount} right`,
        handler: () =>
        {
            sheetCtrl.insertColumns(
                sheetId,
                range.from.colIndex + range.columnsCount,
                range.columnsCount
            );
        }
    };
}

function deleteColumnsItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Delete ${range.columnsCount > 1 ? range.columnsRangeString + ' ' : ''}column${range.columnsCount > 1 ? 's' : ''}`,
        handler: () =>
        {
            sheetCtrl.deleteColumns(
                sheetId,
                range.from.colIndex,
                range.columnsCount,
                false
            );
        }
    };
}

function clearColumnsItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {mesh} = controllers.ui.spreadsheets;

    return {
        label: `Clear ${range.columnsCount > 1 ? range.columnsRangeString + ' ' : ''}column${range.columnsCount > 1 ? 's' : ''}`,
        handler: () => mesh.clearCells(ctx, range, false)
    };
}

function insertRowsItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Insert ${range.rowsCount > 1 ? range.rowsCount + ' ' : ''}row${range.rowsCount > 1 ? 's' : ''}`,
        handler: () =>
        {
            sheetCtrl.insertRows(
                sheetId,
                range.from.rowIndex,
                range.rowsCount
            );
        }
    };
}

function insertRowsAboveItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Insert ${range.rowsCount} above`,
        handler: () =>
        {
            sheetCtrl.insertRows(
                sheetId,
                range.from.rowIndex,
                range.rowsCount
            );
        }
    };
}

function insertRowsBelowItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Insert ${range.rowsCount} below`,
        handler: () =>
        {
            sheetCtrl.insertRows(
                sheetId,
                range.from.rowIndex + range.rowsCount,
                range.rowsCount
            );
        }
    };
}

function insertColumnsItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Insert ${range.columnsCount > 1 ? range.columnsCount + ' ' : ''}column${range.columnsCount > 1 ? 's' : ''}`,
        handler: () =>
        {
            sheetCtrl.insertColumns(
                sheetId,
                range.from.colIndex,
                range.columnsCount
            );
        }
    };
}

function insertCellsRightItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Insert cells: shift right`,
        isDisabled: true,
        handler: () =>
        {
            sheetCtrl.insertCellsRight(
                sheetId,
                range.from.colIndex,
                range.columnsCount
            );
        }
    };
}

function insertCellsDownItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Insert cells: shift down`,
        isDisabled: true,
        handler: () =>
        {
            sheetCtrl.insertCellsDown(
                sheetId,
                range.from.colIndex,
                range.columnsCount
            );
        }
    };
}

function deleteRowsItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {sheetId, sheetCtrl} = ctx;

    return {
        label: `Delete ${range.rowsCount > 1 ? range.rowsRangeString + ' ' : ''}row${range.rowsCount > 1 ? 's' : ''}`,
        handler: () =>
        {
            sheetCtrl.deleteRows(
                sheetId,
                range.from.rowIndex,
                range.rowsCount,
                false
            );
        }
    };
}

function clearRowsItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {mesh} = controllers.ui.spreadsheets;
    return {
        label: `Clear ${range.rowsCount > 1 ? range.rowsRangeString + ' ' : ''}row${range.rowsCount > 1 ? 's' : ''}`,
        handler: () => mesh.clearCells(ctx, range, false)
    };
}

function clearCellsItems(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem[]
{
    const {mesh} = controllers.ui.spreadsheets;

    return [{
        label: `Clear ${range.isSingleCell ? 'cell' : 'cells'}`,
        handler: () => mesh.clearSelectedCells(ctx)
    }, {
        label: `Clear cell ${range.isSingleCell ? 'value' : 'values'}`,
        handler: () => mesh.clearSelectedCellValues(ctx)
    }];
}

function makeCellsLazyItem(ctx:IWSPMeshContext, range:MeshRange):IContextMenuItem
{
    const {mesh} = controllers.ui.spreadsheets;
    const {sheetId, sheetCtrl} = ctx;

    const meshState = mesh.getState(ctx);

    const {details: {activeCell}} = meshState;

    const activeSlot:AnySlot = sheetCtrl.findSlot(sheetId, activeCell.toSlotLocation());
    const makeLazy:boolean = !activeSlot || !activeSlot.isLazy;

    return {
        label: `Make ${makeLazy ? 'action' : 'regular'} ${range.isSingleCell ? 'cell' : 'cells'}`,
        handler: () => mesh.toggleSelectedCellsLaziness(ctx)
    };
}

function gotoBindingItem(binding:IBinding)
{
    const openDesign = controllers.ui.openDesign;

    const box = binding.box;
    if( !box )
        return;

    let component = box.component;
    if( !component.ref.isResolved )
        return null;

    const {bindings} = controllers.ui;

    let itemLabel = doWithBinding(binding,
        (pBinding) => `${component.name} property: "${pBinding.prop.name}${pBinding.propPath.asPropSuffix()}"`,
        (eBinding) => `${component.name} event: "${eBinding.eventProp.name}"`
    );

    if( !itemLabel )
        return null;

    return {
        label: itemLabel,
        handler: () =>
        {
            const {workbenchVisibility, surfaceSelection, surfacePropertiesPanel, surfaceArea, bindings} = controllers.ui;
            const page:Page = box.page;
            const activePage:Page = surfaceArea.getActivePage();

            Assert.yes(activePage);

            if( page !== activePage )
            {
                const overlayIndex:number = commonControllers.designState.getIndexInOverlaysByPage(page);

                if( overlayIndex > -1 )
                    surfaceArea.setActiveOverlay(activePage.overlays[overlayIndex], activePage.id);
                else
                {
                    surfaceArea.setActivePage(page);
                }
            }

            workbenchVisibility.setPropertiesPanelVisibility(true);
            surfaceSelection.setSingleBoxSelection(box);
            surfacePropertiesPanel.setPropertiesPopover(binding.isPropBinding ? PropertiesVisiblePopover.None : PropertiesVisiblePopover.Events);

            doWithBinding<void>(binding,
                pBinding => bindings.editPropBinding(pBinding.prop, pBinding.propPath),
                eBinding => bindings.editEventBinding(eBinding.eventProp)
            );
        }
    };
}

function bindingItems(ctx:IWSPMeshContext, range:MeshRange):Array<IContextMenuItem>
{
    if( !range.isSingleCell )
        return [];

    const bindingsController = controllers.ui.bindings;
    const bindings = bindingsController.findBindingsForLocation(ctx.sheetId, range.from.colIndex, range.from.rowIndex);
    if( !bindings || bindings.length < 1 )
        return [];
    const gotoItems = [];
    for( let binding of bindings )
    {
        const gotoItem = gotoBindingItem(binding);
        if( gotoItem )
            gotoItems.push(gotoItem);
    }

    if( !gotoItems || gotoItems.length < 1 )
        return [];
    return [
        {
            label: 'Go to binding...',
            dataSource: gotoItems
        },
        separator()
    ];
}

function evaluationItems(ctx:IWSPMeshContext, range:MeshRange):Array<IContextMenuItem>
{
    const {mesh} = controllers.ui.spreadsheets;
    const {sheetId, sheetCtrl} = ctx;

    const meshState = mesh.getState(ctx);
    const {size: {numCols, numRows}} = meshState;

    const areSomeCellsLoading = MeshUtils.someCellInRange(range, numRows, numCols, (cellKey:string) =>
    {
        const location:MeshLocation = MeshLocation.fromKey(cellKey);

        return sheetCtrl.isSlotLoading(sheetId, location.toSlotLocation());
    });

    const areSomeCellsNotLoading = MeshUtils.someCellInRange(range, numRows, numCols, (cellKey:string) =>
    {
        const location:MeshLocation = MeshLocation.fromKey(cellKey);

        return !sheetCtrl.isSlotLoading(sheetId, location.toSlotLocation());
    });

    const items = [];

    const reEvaluateCells = {
        label: `Re-evaluate ${range.isSingleCell ? 'cell' : 'cells'}`,
        handler: () => mesh.evaluateSelectedCells(ctx)
    };

    const abortCellsEvaluation = {
        label: `Stop evaluation of ${range.isSingleCell ? 'cell' : 'range'}`,
        handler: () => mesh.abortSelectedCellsEvaluation(ctx)
    };

    if( areSomeCellsNotLoading )
    {
        items.push(reEvaluateCells);
    }

    if( areSomeCellsLoading )
    {
        items.push(abortCellsEvaluation);
    }

    return items;
}

function noteItems(ctx:IWSPMeshContext, range:MeshRange):Array<IContextMenuItem>
{
    if( !range.isSingleCell )
        return [];

    const {colIndex, rowIndex} = range.from;
    const {sheetSettingsCtrl, sheetId} = ctx;

    const cellNote:MeshCellNote = sheetSettingsCtrl.getCellNote(sheetId, new MeshLocation(colIndex, rowIndex).asKey());

    let items = [];

    const {mesh} = controllers.ui.spreadsheets;

    if( cellNote )
    {
        items.push({
            label: 'Edit Notes',
            handler: () => mesh.startEditingOrCreateNote(ctx)
        });
        items.push({
            label: 'Remove Notes',
            handler: () => mesh.removeNote(ctx)
        });
    }
    else
    {
        items.push({
            label: 'Add Notes',
            handler: () => mesh.startEditingOrCreateNote(ctx)
        });
    }

    return [
        ...items,
        separator()
    ];
}

export abstract class MeshContextMenuProvider
{
    public static contextMenuProvider(ctx:IWSPMeshContext):(target:ContextMenuTarget) => DataSource<IContextMenuItem>
    {
        return (target:ContextMenuTarget):DataSource<IContextMenuItem> =>
        {
            const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

            const selectedRange = meshState.details.selectedRange;
            const range = new MeshRange(selectedRange.from, selectedRange.to);

            if( target instanceof Element )
            {
                let targetType:string = null;

                DomUtils.selfAndEachParentElement(target, (elementRef:HTMLElement) =>
                {
                    targetType = elementRef.getAttribute('data-type');
                    return !targetType;
                });

                if( !targetType )
                {
                    return null;
                }

                const menuItems:Array<IContextMenuItem> = [];

                if( meshState.details.isReadonly )
                {
                    switch( targetType )
                    {
                        case MeshViewTargetType.Cell:
                            menuItems.push(EditMenuItems.buildSpreadsheetCopyMenuItem());

                            const bindingMenuItems = bindingItems(ctx, range);
                            if( bindingMenuItems && bindingMenuItems.length > 0 )
                            {
                                menuItems.push(separator());
                                menuItems.push(...bindingMenuItems);
                            }
                            break;
                    }
                }
                else
                {
                    switch( targetType )
                    {
                        case MeshViewTargetType.ColumnHeader:
                            menuItems.push(insertColumnsLeftItem(ctx, range));
                            menuItems.push(insertColumnsRightItem(ctx, range));
                            menuItems.push(separator());
                            menuItems.push(deleteColumnsItem(ctx, range));
                            menuItems.push(separator());
                            menuItems.push(clearColumnsItem(ctx, range));
                            break;
                        case MeshViewTargetType.RowHeader:
                            menuItems.push(insertRowsAboveItem(ctx, range));
                            menuItems.push(insertRowsBelowItem(ctx, range));
                            menuItems.push(separator());
                            menuItems.push(deleteRowsItem(ctx, range));
                            menuItems.push(separator());
                            menuItems.push(clearRowsItem(ctx, range));
                            break;
                        case MeshViewTargetType.Cell:
                            menuItems.push(EditMenuItems.buildSpreadsheetCutMenuItem());
                            menuItems.push(EditMenuItems.buildSpreadsheetCopyMenuItem());
                            // TODO: Finish paste through a fake editable html element
                            menuItems.push(EditMenuItems.buildSpreadsheetPasteMenuItem());
                            menuItems.push(EditMenuItems.buildSpreadsheetPasteValuesOnlyMenuItem());
                            menuItems.push(separator());
                            menuItems.push(...bindingItems(ctx, range));
                            menuItems.push(insertRowsItem(ctx, range));
                            menuItems.push(insertColumnsItem(ctx, range));
                            menuItems.push(insertCellsRightItem(ctx, range));
                            menuItems.push(insertCellsDownItem(ctx, range));
                            menuItems.push(separator());
                            menuItems.push(...noteItems(ctx, range));
                            menuItems.push(deleteRowsItem(ctx, range));
                            menuItems.push(deleteColumnsItem(ctx, range));
                            menuItems.push(separator());
                            menuItems.push(...evaluationItems(ctx, range));
                            menuItems.push(separator());
                            menuItems.push(...clearCellsItems(ctx, range));
                            menuItems.push(separator());
                            menuItems.push(makeCellsLazyItem(ctx, range));
                            break;
                    }
                }

                if( menuItems.length > 0 )
                {
                    return menuItems;
                }
            }

            return null;
        };
    };
}
